import { useState } from 'react';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ViewKanbanOutlinedIcon from '@mui/icons-material/ViewKanbanOutlined';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ClientActivitySchema, ProjectSchema, type ProjectDto } from '@taqon/contracts';
import { Link as RouterLink, useParams } from 'react-router';
import { getData, getPage, patchJson } from '../../shared/api/client';
import { RecordHistory } from '../../shared/audit/AuditTrail';
import { DocumentsPanel } from '../../shared/documents/DocumentsPanel';
import { MigrationTrace } from '../migration/MigrationTrace';
import { hasCapability, useSession } from '../../shared/session/session';
import { StatusChip } from '../../shared/ui/forms';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { ActivityTable } from '../crm/ActivitiesPage';
import { ACTIVITIES, ClientRef, ConfirmDialog, PROJECTS } from '../crm/common';
import { Facts, RecordFailure } from '../finance/detail';
import { businessToday } from '../finance/line-editor';
import { ProjectDialog } from './ProjectsPage';

/**
 * One project (`/projects/:id`; ERD §4.2, FRD-CORE-006, FRD-CRM-033): its client (linked where the
 * session may view clients) and the client activities about it, read from the CRM API that owns
 * them, with edit and activate / deactivate where the session may change projects.
 */
export function ProjectDetailPage() {
  const { id = '' } = useParams();
  const session = useSession();
  const canChange = hasCapability(session.data, 'projects.project:change');
  const [dialog, setDialog] = useState<'edit' | 'status' | null>(null);
  const detail = useQuery({
    queryKey: ['projects', 'one', id],
    queryFn: () => getData(`${PROJECTS}/${id}`, ProjectSchema),
  });
  const back = (
    <Button component={RouterLink} to="/projects" startIcon={<ArrowBackIcon />}>
      All projects
    </Button>
  );
  if (detail.isPending) return <LoadingState label="Loading the project" />;
  if (detail.isError) {
    return <RecordFailure title="Project" noun="project" error={detail.error} back={back} />;
  }
  const project = detail.data;
  return (
    <Stack spacing={3}>
      <PageHeader
        title={`${project.code} — ${project.name}`}
        actions={
          <>
            {back}
            {hasCapability(session.data, 'projects.task:view') && (
              <Button
                variant="outlined"
                component={RouterLink}
                to={`/projects/${project.id}/work`}
                startIcon={<ViewKanbanOutlinedIcon />}
              >
                Open work
              </Button>
            )}
            {canChange && (
              <Button
                variant="outlined"
                color={project.status === 'active' ? 'warning' : 'primary'}
                onClick={(event) => {
                  event.currentTarget.focus();
                  setDialog('status');
                }}
              >
                {project.status === 'active' ? 'Deactivate' : 'Activate'}
              </Button>
            )}
            {canChange && (
              <Button
                variant="contained"
                onClick={(event) => {
                  event.currentTarget.focus();
                  setDialog('edit');
                }}
              >
                Edit
              </Button>
            )}
          </>
        }
      />
      <Section title="Project">
        <Facts
          label="Project details"
          facts={[
            ['Status', <StatusChip key="s" status={project.status} />],
            ['Code', project.code],
            ['Name', project.name],
            ['Description', project.description ?? '—'],
            [
              'Client',
              project.clientId === null ? (
                '—'
              ) : (
                <ClientRef
                  key="c"
                  id={project.clientId}
                  label={project.clientLabel ?? 'Not visible'}
                />
              ),
            ],
          ]}
        />
      </Section>
      {hasCapability(session.data, 'crm.client_activity:view') && (
        <ProjectActivities projectId={project.id} />
      )}
      {/* Scope statements, proposals, sign-offs (FRD-CORE-019). */}
      <DocumentsPanel recordType="projects.project" recordId={project.id} canAttach={canChange} />
      <MigrationTrace recordType="projects.project" recordId={project.id} />
      <RecordHistory recordType="projects.project" recordId={project.id} />
      {dialog === 'edit' && (
        <ProjectDialog
          key={`${project.id}:${String(project.version)}`}
          project={project}
          onClose={() => {
            setDialog(null);
          }}
        />
      )}
      {dialog === 'status' && (
        <ProjectStatusDialog
          project={project}
          onClose={() => {
            setDialog(null);
          }}
        />
      )}
    </Stack>
  );
}

/** The client activities about this project (FRD-CRM-030), newest first. */
function ProjectActivities({ projectId }: { projectId: string }) {
  const session = useSession();
  const activities = useQuery({
    queryKey: ['crm', 'activities', 'project', projectId],
    queryFn: () =>
      getPage(`${ACTIVITIES}?pageSize=200&filter[project_id]=${projectId}`, ClientActivitySchema),
  });
  return (
    <Section title="Client activities">
      {activities.isPending && <LoadingState label="Loading activities" />}
      {activities.isError && <ErrorState message={activities.error.message} />}
      {activities.data?.items.length === 0 && (
        <EmptyState title="No client activities about this project" />
      )}
      {activities.data !== undefined && activities.data.items.length > 0 && (
        <ActivityTable
          label="Project activities"
          activities={activities.data.items}
          showClient
          today={businessToday(session.data?.businessTimezone)}
        />
      )}
    </Section>
  );
}

/** Activates or deactivates a project after a confirmation (FRD-CORE-007). */
function ProjectStatusDialog({ project, onClose }: { project: ProjectDto; onClose: () => void }) {
  const session = useSession();
  const queryClient = useQueryClient();
  const next = project.status === 'active' ? 'inactive' : 'active';
  const save = useMutation({
    mutationFn: () =>
      patchJson(
        `${PROJECTS}/${project.id}`,
        { status: next },
        ProjectSchema,
        session.data?.csrfToken ?? '',
        project.version,
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['projects'] });
      onClose();
    },
  });
  return (
    <ConfirmDialog
      title={next === 'inactive' ? 'Deactivate project' : 'Activate project'}
      message={
        next === 'inactive'
          ? `Deactivate ${project.code}? It stays on existing records but cannot be used for new work.`
          : `Activate ${project.code} again for new work?`
      }
      confirmLabel={next === 'inactive' ? 'Deactivate' : 'Activate'}
      pending={save.isPending}
      error={save.isError ? save.error.message : null}
      onConfirm={() => {
        save.mutate();
      }}
      onClose={onClose}
    />
  );
}
